/**
 * UX 7.1 (D11, Q30) — one person's own read state of one conversation.
 *
 * Stored at `users/{uid}/conversationReads/{conversationId}`. Written ONLY by
 * the `markConversationRead` callable (Admin SDK); the owner may read it, no
 * client may write it (firestore.rules).
 *
 * A company's order / object / general threads are shared by a mutable team,
 * so the shared `clientUnread` counter could only say "somebody in the
 * company has read this" — opening a thread cleared it for every colleague.
 * The personal answer is:
 *
 *   unread  ⇔  the last message was written by somebody else
 *              AND it is newer than `lastReadAt`.
 *
 * Without a read document yet, the person has never opened the thread; the
 * shared counter (`clientUnread > 0`, still written by postMessage) is then
 * the best available signal — see `isConversationUnreadFor`.
 */
export interface ConversationRead {
  conversationId: string;
  /**
   * ISO instant up to which this person has read: the thread's
   * `lastMessage.at` when they opened it (never "now" — a message that
   * arrived while the list was on screen stays unread).
   */
  lastReadAt: string;
  /** ISO, server-set. */
  updatedAt: string;
}

/** Firestore Timestamp | ISO string | missing → epoch millis (0 when unknown). */
export function conversationInstantMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return Date.parse(v) || 0;
  if (typeof v === 'object') {
    const o = v as { toMillis?: () => number; seconds?: number; _seconds?: number };
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o.seconds === 'number') return o.seconds * 1000;
    if (typeof o._seconds === 'number') return o._seconds * 1000;
  }
  return 0;
}

/** The fields of a conversation the personal unread answer needs. */
export interface UnreadSubject {
  lastMessage?: { byUid?: string; at?: unknown } | null;
  clientUnread?: number;
  /** A thread folded into another (order group, UX 7.3) is never unread. */
  mergedInto?: string;
}

/**
 * Is this thread unread FOR THIS PERSON? Pure — the client badge, the inbox
 * row and the server's `counts.messages` all ask this one function.
 *
 * `readAt` is the person's `lastReadAt` (ISO) or undefined when they have no
 * read document for the thread.
 */
export function isConversationUnreadFor(
  conv: UnreadSubject,
  uid: string,
  readAt: string | undefined,
): boolean {
  if (conv.mergedInto) return false;
  const last = conv.lastMessage;
  if (!last) return false;
  // My own message is never unread to me.
  if (last.byUid && last.byUid === uid) return false;
  if (readAt !== undefined) {
    return conversationInstantMillis(last.at) > conversationInstantMillis(readAt);
  }
  // Never opened: fall back to the shared counter, which postMessage still
  // bumps for every RM24 post and which a client read no longer clears.
  return (conv.clientUnread ?? 0) > 0;
}

/** How many of these threads are unread for this person. */
export function countConversationsUnreadFor(
  convs: readonly (UnreadSubject & { id: string })[],
  uid: string,
  reads: ReadonlyMap<string, string>,
): number {
  let n = 0;
  for (const c of convs) if (isConversationUnreadFor(c, uid, reads.get(c.id))) n += 1;
  return n;
}
